import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// TS types
import { WindowCustomData } from 'page/detail/index';
import { Area, IPlanList, IListProps, scrollToRef, ICalendar } from '@redux/common';
import { ICommonStore } from '@redux/Common/interfaces';
import { ITHSRStore } from '@redux/THSR/interfaces';
// Redux
import { RootState } from '@redux/store';

// import module
import NvbRslb from '@magaele/nvb_rslb';

// component
import DetailInfo from './subComponent/DetailInfo';

// shared component
import Reload from '@svg/Reload';
import MobileHeader from '@component/Common/mobileHeader';
import SlideFooter from '@component/Common/SlideFooter';
import Dialog from '@component/Common/Dialog';

// common function
import { fetchDetailData, fetchLightBoxData } from '@libs/apiCall'; //TODO:第二階段要加入session 打API的事件
import {
    addSeparator,
    getWindowCustomFunctions,
    decodeHTMLstring,
    handletBodyFixed,
    initTicketNum,
    convertTicketNum,
    getEarliestDate,
    THSRClearStorage,
    checkLigBoxOrDialogDisplay,
} from '@libs/core';
import { isMobile } from '@libs/globalVariables';
import onSubmit from './Common/onSubmit';

// plugin
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

// redux actions
import {
    setPickCalendar,
    setDetailData,
    setTempCalendarData,
    setTicketNum,
    setTotalAmount,
    setNowListID,
    setNowListData,
    setIsDetailLoading,
    setDetailLoadingFail,
    setIsSessionLoading,
    setSessionLoadingFail,
    setIsSubmitSuccess,
    setAllSessions,
    setPickSession,
    setDisabledSessions,
    setMinDate,
    setNullDate,
    setDisabledDate,
    setSoldoutDate,
    setDisabledDateBySession,
    setOpenNvbRslb,
    setTicketErrorMsg,
    setCalendarErrorMsg,
    setSessionErrorMsg,
    setScrollTo,
    setReset,
    setRestore,
    setETItemData,
} from '@redux/Normal/actions';
import { setFocusArea, setDetailFetchAllData, setDetailFetchFailMsg } from '@redux/Common/actions';
import AddOnPriceTag from '@component/Common/AddOnPriceTag';

const Plan: React.FC<IListProps> = (props) => {
    const dispatch = useDispatch();
    const { formatPriceByCulture }: WindowCustomData = getWindowCustomFunctions();
    const { t } = useTranslation();
    const { Currency, isCrossell } = props;
    const {
        FormatID,
        FormatName,
        LowListPrice,
        LowSalePrice,
        EarliestBuyDay,
        IntroductionArray,
        ExpectedUseDay,
        IsForSale,
        NotForSaleReason,
    } = props.data;
    const {
        NowListID,
        NowListData,
        DetailData,
        pickCalendar,
        ticketNum,
        pickSession,
        allSessions,
        isDetailLoading,
        DetailLoadingFail,
        isSessionLoading,
        SessionLoadingFail,
        isSubmitSuccess,
        ticketErrorMsg,
        calendarErrorMsg,
        sessionErrorMsg,
        nullDate,
        soldoutDate,
        openNvbRslb,
        ETItemData,
    } = useSelector((state: RootState) => state.Normal);
    const {
        Foreign,
        ETID,
        isLogin,
        isDisney,
        DetailFetchAllData,
        listStatus,
        ListData,
        DetailFetchMonths, // 判斷切換月份是否打API的依據，2023/04/11停止使用
        DetailFetchFailMsg,
        VerificationStatus,
    }: ICommonStore = useSelector((state: RootState) => state.Common);
    const { THSRInitData }: ITHSRStore = useSelector((state: RootState) => state.THSR);
    const hasCalendar = NowListData?.ExpectedUseDay;
    const hasSession = DetailData?.Sessions;
    const SaleCurrency = props.SaleCurrency;
    const { ItemType, AdvanceTime }: IPlanList = ListData;
    /*-------- 自建高鐵 無FormatID 使用的參數 -------*/
    const { TotalLowSalePrice, TotalEarliestBuyDay, TotalIntroductionArray }: IPlanList = ListData;
    const LSPrice = FormatID === 'Default' ? TotalLowSalePrice : LowSalePrice;
    const EBDay = FormatID === 'Default' ? TotalEarliestBuyDay : EarliestBuyDay;
    const IntroArray = FormatID === 'Default' ? TotalIntroductionArray : IntroductionArray;
    /*---------------------------------------------*/
    const listBlockRef = useRef(null);

    const [dialogSwitch, setDialogSwitch] = useState<{ [key: string]: boolean }>({}); //Dialog-State object
    const [fetchLightBoxResult, setFetchLightBoxResult] = useState({}); //Fetch lightBox save result state

    // _start：迪士尼光箱開發區
    const lightBoxMockData: {
        [key: string]: {
            title?: string;
            content?: string;
        };
    }[] = [
        fetchLightBoxResult, //Fetch json
    ];
    const disneyLightBoxData = lightBoxMockData[0].disney;
    // console.log('disneyLightBoxData', disneyLightBoxData);
    // console.log('fetchLightBoxResult!', fetchLightBoxResult);
    // console.log('dialogSwitch', dialogSwitch);
    // _END：迪士尼光箱開發區

    // Fetch LightBox object data & init dialog state
    useEffect(() => {
        const getLightBoxData = async () => {
            const getData = await fetchLightBoxData();
            const result = await getData.json();

            setFetchLightBoxResult(result);
        };

        getLightBoxData();
        /** 初始化 dialog state
         *  無法一開始就取得資料的光箱才在下面新增，其他等打完API後再在useEffect做資料確認&新增
         */
        setDialogSwitch({
            // 預設基本會有的光箱類型
            verify: false, // 驗價光箱
        });
    }, []);

    // Dialog component switch object settings
    useEffect(() => {
        /* 一開始就可以打API拿到資料的光箱 */
        //迪士尼對話框光箱
        if (isDisney && !!disneyLightBoxData && Object.keys(disneyLightBoxData).length) {
            setDialogSwitch((prev) => ({ ...prev, disney: false }));
        }
    }, [isDisney, disneyLightBoxData, fetchLightBoxResult]);

    useEffect(() => {
        if (!DetailData) return;
        // 初始化當月場次資料(自建第一、二階段用)
        const initialSessionData = (CalendarDate: ICalendar) => {
            let firstDay = getEarliestDate(Object.keys(CalendarDate)); // 抓可選日期的第一天
            let sessions = CalendarDate[firstDay].Events;

            if (sessions.length > 0) {
                dispatch(setAllSessions(sessions));
                dispatch(setPickSession(sessions[0])); //預設選定第一筆session
            }
        };

        // 更新ETITEM
        if (ExpectedUseDay) {
            //沒場次的直接設定選取日的ETITEM，有場次的在setSession後，會在TicketBlock裡抓值重新render[事件1]

            // if (!DetailData?.Sessions) {
            //     Object.keys(DetailData.CalendarDate).length !== 0
            //     ? dispatch(setTicketNum(convertTicketNum(DetailData.CalendarDate[pickCalendar]?.ETItem)))
            //     : dispatch(setTicketNum(convertTicketNum(DetailData.ETItem)))
            // }
            /*-----自建第一階段使用 (TODO: 改完場次用上面的)------*/
            Object.keys(DetailData.CalendarDate).length !== 0
                ? dispatch(
                      setTicketNum(convertTicketNum(DetailData.CalendarDate[pickCalendar]?.ETItem))
                  )
                : dispatch(setTicketNum(convertTicketNum(DetailData.ETItem)));
            /*------------------------------------*/
        } else {
            dispatch(setTicketNum(convertTicketNum(DetailData.ETItem)));
        }

        // 初始化場次 TODO:第三階段改成打API
        if (hasSession) {
            if (Object.keys(DetailData.CalendarDate).length !== 0)
                initialSessionData(DetailData.CalendarDate);
        }
    }, [dispatch, DetailData, hasSession, ExpectedUseDay, pickCalendar]);

    // PC設定是否預設展開
    useEffect(() => {
        if (!listStatus || !ListData || isMobile) return;

        if (THSRInitData?.HasTHSR) {
            if (listStatus.originQty !== 1) return;
            if (listStatus.onewayQty !== 0 || listStatus.roundQty !== 0) return;
        } else {
            if (listStatus.originQty !== 1) return;
            // if (ListData.Format?.length > 1) return;
        }

        openOption();
    }, [listStatus, THSRInitData, ListData]);

    /**
     * [日庫存]目前在
     * 1.更換月份及跨月機制(更新detailData)
     * 2.點選日期
     * 都會更新 TicketNum
     * TODO: 之後場次API完成後檢查，沒問題就刪除
     */
    //01/13 IT提出[日庫存]：如果該產品為有月曆的產品，當所選日期有變動時，要更新篩選資料
    // useEffect(() => {
    //     if (DetailData?.CalendarDate) {
    //         let ETItem = pickCalendar
    //             ? DetailData?.CalendarDate[pickCalendar].ETItem
    //             : DetailData.ETItem;
    //         let newTicketNum = { ...ticketNum };

    //         ETItem.forEach((element) => {
    //             if (newTicketNum[element.ItemSeq] > element.HighQty) {
    //                 newTicketNum[element.ItemSeq] = element.HighQty;
    //             }
    //         });
    //         ticketNum && dispatch(setTicketNum(newTicketNum));
    //     }
    // }, [pickCalendar]);

    /*===========================根據選取條件篩選資料=======================*/
    //點選 票券數量 後的篩選
    // useEffect(() => {
    //     if (ticketNum && filterData) {
    //         let keys = Object.keys(ticketNum);
    //         let filterDates = [];
    //         filterDates = keys.map((itemSeq) => {
    //             //用票券數量篩選出各票券可使用日期及場次
    //             return filterData[itemSeq][ticketNum[itemSeq]];
    //         });
    //         let _disabledDate = []; //用票券數量篩選出各票券不可使用日期及場次
    //         filterDates.forEach((itemSeq) => {
    //             _disabledDate = [
    //                 ..._disabledDate,
    //                 ...allDates.current.filter((date) => !Object.keys(itemSeq).includes(date)),
    //             ];
    //         });
    //         setFilterDays(filterDates);
    //         _disabledDate = Array.from(new Set(_disabledDate));
    //         dispatch(setDisabledDate(_disabledDate));
    //         // changeCalendarData(_disabledDate);
    //     }
    // }, [ticketNum, DetailData, filterData]);

    //點選 日期或場次 後的篩選
    // useEffect(() => {
    //     if(!!DetailData){
    //         if (!pickCalendar && !pickSession) {
    //             //無選取日期及場次
    //             dispatch(setDisabledDateBySession([]));
    //             dispatch(setDisabledSessions([]));
    //             dispatch(setETItemData(ETItem));
    //         } else if (!pickCalendar && pickSession) {
    //             //無選取日期但有場次
    //             let _disabledDateBySession = [];
    //             filterDays.forEach((item) => {
    //                 _disabledDateBySession = Array.from(
    //                     new Set([
    //                         ..._disabledDateBySession,
    //                         ...Object.keys(item).filter((date) => {
    //                             return !item[date].includes(pickSession);
    //                         }),
    //                     ])
    //                 );
    //             });
    //             dispatch(setDisabledDateBySession(_disabledDateBySession));
    //             dispatch(setDisabledSessions([]));
    //             dispatch(setETItemData(ETItem));
    //         } else if (pickCalendar && !pickSession) {
    //             //有選取日期,但未選取場次
    //             dispatch(setETItemData(DetailData.CalendarDate[pickCalendar].ETItem));
    //             let disaabledSessions = allSessions?.filter(
    //                 (session) => !DetailData.CalendarDate[pickCalendar].Events.includes(session)
    //             );
    //             dispatch(setDisabledDateBySession([]));
    //             dispatch(setDisabledSessions(disaabledSessions));
    //         } else {
    //             let _disabledDateBySession = [];
    //             filterDays.forEach((item) => {
    //                 _disabledDateBySession = Array.from(
    //                     new Set([
    //                         ..._disabledDateBySession,
    //                         ...Object.keys(item).filter((date) => {
    //                             return !item[date].includes(pickSession);
    //                         }),
    //                     ])
    //                 );
    //             });
    //             dispatch(setDisabledDateBySession(_disabledDateBySession));
    //             let disaabledSessions = allSessions?.filter(
    //                 (session) => !DetailData.CalendarDate[pickCalendar].Events.includes(session)
    //             );
    //             dispatch(setDisabledSessions(disaabledSessions));
    //             dispatch(setETItemData(DetailData.CalendarDate[pickCalendar].ETItem));
    //         }
    //     }
    // }, [pickCalendar, pickSession, filterDays]);
    /*====================================================================*/

    // 檢查面版是否有加購價
    const [showCrossellTag, setShowCrossellTag] = useState(true);

    // 加購價省下的金額
    const [salePriceSpread, setSalePriceSpread] = useState(0);

    // 更新加總價格
    useEffect(() => {
        // 加總
        const calculateAmount = (pickCalendar: string) => {
            // TODO: 第二階段處理
            // const ETdata = ExpectedUseDay
            //     ? DetailData.Sessions
            //         ? getSessionInfo({ session, sessionPicked })?.ETItem
            //         : GDetail.CalendarDate[pickCalendar]?.ETItem
            //     : GDetail.ETItem;

            // TODO: 因sonarQube會擋邏輯，所以待IT開發完Session API，在做調整
            // const ETdata = ExpectedUseDay
            //     ? DetailData.Sessions
            //         ? DetailData.CalendarDate[pickCalendar]?.ETItem
            //         : DetailData.CalendarDate[pickCalendar]?.ETItem
            //     : DetailData.ETItem;

            const ETdata = ExpectedUseDay
                ? DetailData.CalendarDate[pickCalendar]?.ETItem
                : DetailData.ETItem;
            if (!ETdata) return 0;

            const hasSalePriceSpread = ETdata.some((item) => item.SalePrice_Spread > 0);
            setShowCrossellTag(hasSalePriceSpread);

            let totalAmount = 0;
            let totalSpread = 0;
            const keys = Object.keys(ticketNum);

            if (isLogin) {
                keys.forEach((key) => {
                    let obj = ETdata.find((item) => item.ItemSeq === key);
                    if (obj) {
                        let price = obj.SalePrice !== 0 ? obj.SalePrice : obj.SalePrice_NonMember;
                        if (isCrossell) {
                            totalSpread += ticketNum[key] * obj.SalePrice_Spread;
                            price -= obj.SalePrice_Spread;
                        }
                        totalAmount += ticketNum[key] * price;
                    }
                });
            } else {
                keys.forEach((key) => {
                    let obj = ETdata.find((item) => item.ItemSeq === key);
                    if (obj) {
                        let price = obj.SalePrice_NonMember;
                        if (isCrossell) {
                            totalSpread += ticketNum[key] * obj.SalePrice_Spread;
                            price -= obj.SalePrice_Spread;
                        }
                        totalAmount += ticketNum[key] * price;
                    }
                });
            }

            setSalePriceSpread(totalSpread);
            return totalAmount;
        };

        if (!!ticketNum) {
            const amount = calculateAmount(pickCalendar);
            dispatch(setTotalAmount(addSeparator(formatPriceByCulture(Currency, amount))));
            if (amount > 0) dispatch(setTicketErrorMsg(''));
        } else {
            dispatch(setTotalAmount('0'));
        }
    }, [dispatch, ExpectedUseDay, DetailData, ticketNum, pickCalendar, isLogin, isCrossell]);

    // 清單牌價顯示規則
    const ShouldOriginPriceShow = (() => {
        if (ItemType === '0') return false; //自建沒有牌價顯示
        if (LowListPrice === 0 || !LowListPrice) return false;
        switch (Foreign) {
            case '0':
                if (LowSalePrice >= LowListPrice) return false;
                break;
            case '1':
            default:
                return true;
        }
        return true;
    })();

    // 打開方案
    const openOption = () => {
        //已售完的話就不要打開方案
        if (!IsForSale) return;
        /*
        // 2023/04/11 與IT討論，決定改成切換月份都會重打，並且detail API -> EarliestBuyDay改成固定
        // 若為多品項產品，切換品項後要先刪除該品項之前打過的月份紀錄
        const copyDetailFetchMonths = {...DetailFetchMonths}
        const existID = Object.keys(copyDetailFetchMonths).find((saveID)=> saveID === FormatID)
        if(existID){
            copyDetailFetchMonths[existID] = []
            dispatch(setDetailFetchMonths(copyDetailFetchMonths))
        }
        */

        const fetchDetailProps = {
            ETID,
            EarliestBuyDay: EBDay,
            ExpectedUseDay,
            FormatID,
            isTHSR: false,
            // DetailFetchMonths: copyDetailFetchMonths,
            context: {
                DetailFetchAllData,
                setDetailFetchAllData,
                setDetailLoadingFail,
                setDetailFetchFailMsg,
                setDetailData,
                setTempCalendarData,
                setIsDetailLoading,
                setPickCalendar,
                // setDetailFetchMonths,
                ThsrRoundType: null,
                // TODO:第二階段處理
                setIsSessionLoading,
                setSessionLoadingFail,
                setAllSessions,
                setPickSession,
            },
            dispatch,
        };

        dispatch(setNowListData(props.data));
        dispatch(setNowListID(FormatID));
        dispatch(setFocusArea(Area.Origin));
        fetchDetailData(fetchDetailProps);
    };

    // M版用於滑動到特定區塊
    const handleScrollTo = () => {
        if (!isMobile) return;
        if (hasCalendar) {
            dispatch(setScrollTo(scrollToRef.calender));
        } else {
            dispatch(setScrollTo(scrollToRef.ticket));
        }
    };

    return (
        <div
            className={cx('plan', 'normalPlan', {
                opened: NowListID === FormatID,
                closed: NowListID !== FormatID,
            })}
            ref={listBlockRef}
            data-index={0}
        >
            <div className="showInfo">
                <div className="leftPart">
                    {<div className="title">{FormatName}</div>}
                    {isMobile && !AdvanceTime && (
                        <div className="firstOrderDate">
                            {!ExpectedUseDay
                                ? ''
                                : EBDay
                                ? `${t('noTHSR.plan.EarliestUseDay', { symbol: ':' })} ${EBDay}`
                                : t('noTHSR.plan.thisPlanIsSoldOut')}
                        </div>
                    )}
                    {IntroArray && (
                        <div className="notice_wrap">
                            <ul className="notice">
                                {IntroArray?.map((notice, i) => {
                                    const htmlStr = decodeHTMLstring(notice);
                                    return (
                                        <li key={i}>
                                            <span
                                                dangerouslySetInnerHTML={{ __html: htmlStr }}
                                            ></span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>
                <div className="rightPart">
                    <div className="pickBlock">
                        <div className="price">
                            <div className="sale">
                                {isCrossell && showCrossellTag && (
                                    <div className="addOnPrice">
                                        <AddOnPriceTag />
                                    </div>
                                )}
                                <span className="saleCurrency">{SaleCurrency}</span>
                                <span className="salePrice">{addSeparator(LSPrice)}</span>
                                <span className="priceLabel">{t('noTHSR.plan.up')}</span>
                            </div>
                            {/* 原價 */}
                            {ShouldOriginPriceShow && (
                                <div className="original">
                                    <span>{SaleCurrency}</span>
                                    <span>{addSeparator(LowListPrice)}</span>
                                </div>
                            )}
                        </div>
                        <div
                            className={cx('selectedBtn', {
                                disabled: isDetailLoading,
                                secondary: NowListID === FormatID && !isDetailLoading,
                                allsold: !IsForSale,
                            })}
                            onClick={() => {
                                if (isDetailLoading || !IsForSale) return;
                                if (!isMobile && NowListID === FormatID) {
                                    dispatch(setNowListID(null));
                                    dispatch(setNowListData(null));
                                    dispatch(setFocusArea(Area.None));
                                    return;
                                }
                                if (isMobile) {
                                    dispatch(setOpenNvbRslb(true));
                                    handletBodyFixed(true);
                                } else {
                                    // 滑動到點選的方案
                                    const listOpend = document.querySelector('.opened');
                                    const clickedRef = listBlockRef.current;
                                    const selectIndex = clickedRef.getAttribute('data-index');
                                    const OpendedIndex = listOpend?.getAttribute('data-index');
                                    if (listOpend && Number(selectIndex) > Number(OpendedIndex)) {
                                        // 如果新點選的方案在已展開方案的後面則先直接跳動到前一個方案再往下滑
                                        // 避免因要同時收合已展開方案造成高度計算錯誤而無法scroll到正確位置
                                        clickedRef.previousSibling?.scrollIntoView({
                                            behavior: 'auto',
                                        });
                                        setTimeout(() => {
                                            clickedRef.scrollIntoView({
                                                behavior: 'smooth',
                                            });
                                        }, 50);
                                    } else {
                                        // 其餘不會因收合造成計算高度錯誤的情況可直接smooth scroll
                                        clickedRef.scrollIntoView({
                                            behavior: 'smooth',
                                        });
                                    }
                                }
                                calendarErrorMsg && dispatch(setCalendarErrorMsg(''));
                                sessionErrorMsg && dispatch(setSessionErrorMsg(''));
                                ticketErrorMsg && dispatch(setTicketErrorMsg(''));
                                DetailFetchFailMsg && dispatch(setDetailFetchFailMsg(null));
                                openOption();
                            }}
                        >
                            {isDetailLoading ? (
                                <>
                                    <span>
                                        {IsForSale
                                            ? t('noTHSR.plan.choose', { particle: '' })
                                            : NotForSaleReason}
                                    </span>
                                    {NowListID === FormatID && (
                                        <div className="reload">
                                            <Reload color="#f03742" />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <span>
                                        {NowListID !== FormatID || isMobile
                                            ? IsForSale
                                                ? t('noTHSR.plan.choose', { particle: '' })
                                                : NotForSaleReason
                                            : t('noTHSR.plan.choose_cancel')}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    {!isMobile && !AdvanceTime && (
                        <div className="firstOrderDate">
                            {!ExpectedUseDay
                                ? ''
                                : EBDay
                                ? `${t('noTHSR.plan.EarliestUseDay', { symbol: ':' })}${EBDay}`
                                : t('noTHSR.plan.thisPlanIsSoldOut')}
                        </div>
                    )}
                </div>
            </div>
            {!isMobile &&
                NowListID === FormatID &&
                !isDetailLoading &&
                (DetailLoadingFail ? (
                    <div>
                        {DetailFetchFailMsg
                            ? t(`common.${DetailFetchFailMsg}`)
                            : t('common.defaultErrMsg')}
                    </div>
                ) : (
                    <DetailInfo
                        dialogSwitch={dialogSwitch}
                        setDialogSwitch={setDialogSwitch}
                        isCrossell={isCrossell}
                        salePriceSpread={salePriceSpread}
                        showCrossellTag={showCrossellTag}
                    />
                ))}
            {/* M側開 */}
            {isMobile && (
                <NvbRslb
                    // 多品項時，要判斷相同ID才顯現，不然模組會開啟渲染的最後一個(Z-index)
                    visible={openNvbRslb && !isDetailLoading && NowListID === FormatID}
                    direction="right"
                    className={cx('normalPlanM', 'plansM')}
                >
                    <div className="mobileBlock">
                        <MobileHeader
                            title={t('noTHSR.plan.choose_plan')}
                            onLeftIconClick={() => {
                                dispatch(setRestore()); //跳出方案側開要清空所有detail的值
                                handletBodyFixed(false);
                                dispatch(setOpenNvbRslb(false));
                                dispatch(setFocusArea(Area.None));
                            }}
                        />
                        <div className="contentWrap">
                            <div className="mainBLock">
                                <div className="showInfo">
                                    {
                                        <div className="rightPart">
                                            <div className="firstOrderDate">
                                                {!ExpectedUseDay
                                                    ? ''
                                                    : EBDay
                                                    ? `${t('noTHSR.plan.EarliestUseDay', {
                                                          symbol: ':',
                                                      })}${EBDay}`
                                                    : t('noTHSR.plan.thisPlanIsSoldOut')}
                                            </div>
                                            {NowListID === FormatID ? (
                                                DetailLoadingFail ? (
                                                    <div>
                                                        {DetailFetchFailMsg
                                                            ? t(`common.${DetailFetchFailMsg}`)
                                                            : t('common.defaultErrMsg')}
                                                    </div>
                                                ) : (
                                                    <DetailInfo
                                                        dialogSwitch={dialogSwitch}
                                                        setDialogSwitch={setDialogSwitch}
                                                        isCrossell={isCrossell}
                                                        salePriceSpread={salePriceSpread}
                                                        showCrossellTag={showCrossellTag}
                                                    />
                                                )
                                            ) : null}
                                        </div>
                                    }
                                </div>
                            </div>
                            {isMobile &&
                                !isDetailLoading &&
                                !isSessionLoading &&
                                !DetailLoadingFail && (
                                    <SlideFooter
                                        hide={!NowListID}
                                        onClickLeftBtn={() => {
                                            dispatch(setReset());
                                            handleScrollTo();
                                        }}
                                        onClickRightBtn={async () => {
                                            try {
                                                if (
                                                    isSubmitSuccess ||
                                                    !IsForSale ||
                                                    AdvanceTime?.length > 0
                                                )
                                                    return;
                                                // 送出前清空高鐵相關sessionStorage
                                                THSRClearStorage();
                                                const isSuccess = await onSubmit('checkData');
                                                if (typeof isSuccess === 'undefined') return;
                                                if (isSuccess.selectChecked) {
                                                    // 確認是否為迪士尼產品
                                                    if (
                                                        checkLigBoxOrDialogDisplay(
                                                            dialogSwitch,
                                                            setDialogSwitch,
                                                            'disney'
                                                        )
                                                    ) {
                                                        if (!isSubmitSuccess) {
                                                            setDialogSwitch((prev) => {
                                                                prev.disney = true;
                                                                return { ...prev };
                                                            });
                                                        }
                                                    } else {
                                                        dispatch(setIsSubmitSuccess(true));
                                                        onSubmit('redirect');
                                                    }
                                                } else if (
                                                    !isSuccess.selectChecked &&
                                                    'IsSuccess' in isSuccess.apiChecked &&
                                                    !isSuccess.apiChecked.IsSuccess
                                                ) {
                                                    // 驗價錯誤則顯示提示重整頁面光箱
                                                    setDialogSwitch((prev) => {
                                                        prev.verify = true;
                                                        return { ...prev };
                                                    });
                                                }
                                            } catch (e) {
                                                throw e;
                                            }
                                        }}
                                        leftBtnText={t('noTHSR.plan.allReset')}
                                        rightBtnClass={[
                                            'planFoot',
                                            {
                                                notReady: AdvanceTime.length > 0,
                                                disabled: isSubmitSuccess,
                                                allsold: !IsForSale,
                                            },
                                        ]}
                                        rightBtnJSX={
                                            <div className={cx('confirm')}>
                                                <div>
                                                    {AdvanceTime.length > 0
                                                        ? t('noTHSR.plan.comingSoon')
                                                        : !IsForSale
                                                        ? t('noTHSR.plan.restocking')
                                                        : t('noTHSR.plan.bookNow')}
                                                </div>
                                                {isSubmitSuccess && (
                                                    <div className="reload">
                                                        <Reload color="#ffffff" />
                                                    </div>
                                                )}
                                            </div>
                                        }
                                    />
                                )}
                        </div>
                    </div>
                </NvbRslb>
            )}
            {/* 迪士尼對話框光箱 */}
            <Dialog
                type="default"
                title={disneyLightBoxData?.title ?? ''}
                body={disneyLightBoxData?.content ?? ''}
                open={dialogSwitch.disney}
                onClose={() => {
                    console.log('Close Disney LightBox!');
                    dispatch(setIsSubmitSuccess(false));
                    setDialogSwitch((prev) => {
                        prev.disney = false;
                        return { ...prev };
                    });
                }}
                // onCancel={() => {
                //     console.log('cancel!')
                //     console.log(dialogSwitch)
                //     setDialogSwitch(prev => {
                //         prev.disney = false;
                //         return {...prev};
                //     })
                // }}
                onOK={() => {
                    console.log('Disney CheckOK!');
                    dispatch(setIsSubmitSuccess(true));
                    setDialogSwitch((prev) => {
                        prev.disney = false;
                        return { ...prev };
                    });
                    onSubmit('redirect');
                }}
                OKText={t('noTHSR.plan.IHaveRead')}
                showCloseButton={true}
                className={cx('disney')}
            />
            {/* 驗價提示光箱 */}
            <Dialog
                type="default"
                title={VerificationStatus?.Title ?? ''}
                body={VerificationStatus?.ErrMessage ?? 'fetch API error, please reset page'}
                open={dialogSwitch.verify}
                onClose={() => {
                    console.log('Close Verify LightBox!');
                    dispatch(setIsSubmitSuccess(false));
                    setDialogSwitch((prev) => {
                        prev.verify = false;
                        return { ...prev };
                    });
                }}
                onOK={() => {
                    // 按下已閱讀後重新整理頁面
                    console.log('validate error, reset detailPage...');
                    setDialogSwitch((prev) => {
                        prev.verify = false;
                        return { ...prev };
                    });
                    window.location.reload();
                }}
                OKText={t('noTHSR.plan.IHaveRead')}
                showCloseButton={true}
                className={cx('orderValidity')}
            />
        </div>
    );
};
export default Plan;
